import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL;

export interface SettlementRecord {
  settledAt: string | null;
  settledByName: string | null;
  hostId: string | null;
  hostName: string | null;
  note: string | null;
  reminderSentAt: string | null;
}

export interface ActivityWeek {
  activityId: string;
  activityTitle: string;
  activityTitleCN?: string | null;
  activityTitleEN?: string | null;
  weekStart: string;
  weekEnd: string;
  hostId: string;
  hostName: string;
  sessionCount: number;
  bookingCount: number;
  amount: number; // cents
  settled: boolean;
  settlement: SettlementRecord | null;
}

export interface SettlementWeek {
  weekStart: string;
  weekEnd: string;
  totalCount: number;
  settledCount: number;
  activityWeeks: ActivityWeek[];
}

export interface SettlementListResult {
  weeks: SettlementWeek[];
  olderUnsettled: SettlementWeek[];
  windowStart: string;
  hasOlder: boolean;
  startWeek: string;
}

export class SettlementApiError extends Error {
  code: string;
  status: number;
  constructor(code: string, status: number, message: string) {
    super(message);
    this.name = 'SettlementApiError';
    this.code = code;
    this.status = status;
  }
}

async function getToken(): Promise<string | undefined> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });
  if (res.status === 204) return undefined as T;

  interface Envelope {
    success?: boolean;
    data?: unknown;
    error?: { code?: string; message?: string } | string;
    message?: string;
  }
  let body: Envelope | null = null;
  try {
    body = (await res.json()) as Envelope;
  } catch {
    // status-based error below
  }
  if (!res.ok || body?.success === false) {
    const err = body?.error;
    const code = typeof err === 'object' && err?.code ? err.code : 'ERROR';
    const message =
      (typeof err === 'string' ? err : err?.message) ?? body?.message ?? `HTTP ${res.status}`;
    throw new SettlementApiError(code, res.status, message);
  }
  return body?.data as T;
}

export const settlementApi = {
  list: (opts: { before?: string; unsettledOnly?: boolean } = {}) => {
    const params = new URLSearchParams();
    if (opts.before) params.set('before', opts.before);
    if (opts.unsettledOnly) params.set('unsettledOnly', 'true');
    const qs = params.toString();
    return request<SettlementListResult>(`/admin/settlements${qs ? `?${qs}` : ''}`);
  },

  mark: (activityId: string, weekStart: string, note?: string) =>
    request<SettlementRecord>(`/admin/settlements/${activityId}/${weekStart}`, {
      method: 'POST',
      body: JSON.stringify(note ? { note } : {}),
    }),

  undo: (activityId: string, weekStart: string) =>
    request<void>(`/admin/settlements/${activityId}/${weekStart}`, { method: 'DELETE' }),

  /** Download the settlement CSV: the bookings export in Session Date mode for one activity week. */
  exportCsv: async (row: ActivityWeek): Promise<void> => {
    const token = await getToken();
    const params = new URLSearchParams({
      dateFilterType: 'session',
      variant: 'settlement',
      activityId: row.activityId,
      startDate: row.weekStart,
      endDate: row.weekEnd,
    });
    const res = await fetch(`${API_URL}/admin/bookings/export?${params.toString()}`, {
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    });
    if (!res.ok) throw new SettlementApiError('EXPORT_FAILED', res.status, 'Failed to export');
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    // Activity title in the filename, minus characters filesystems reject.
    const safeTitle = row.activityTitle.replace(/[\\/:*?"<>|]/g, '').trim() || row.activityId;
    a.download = `settlement-${row.weekStart}-${safeTitle}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(blobUrl);
    document.body.removeChild(a);
  },
};
